import { useEffect, useState } from 'react';

import {
  Clock02Icon,
  Delete02Icon,
  PencilEdit01Icon,
  PowerIcon,
  ReplayIcon,
  ZapIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useToast } from 'hooks/toast';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { RenewalConfigModal } from './components/RenewalConfigModal/RenewalConfigModal';
import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { Button, SearchInput } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import {
  useCreateRenewalConfigMutation,
  useDeleteRenewalConfigMutation,
  useUpdateRenewalConfigMutation,
} from 'api/renewals-config-api';

import styles from './RenewalsConfigurationTab.module.scss';

export const RenewalsConfigurationTab = ({
  companyChecks = [],
  renewalConfigs = [],
  companyUsers = [],
  brands = [],
  setLoading = () => {},
  updateLocalRenewalConfigs = () => {},
}) => {
  const { t } = useTranslation();
  const showToast = useToast();

  const [createRenewalConfig] = useCreateRenewalConfigMutation();
  const [updateRenewalConfig] = useUpdateRenewalConfigMutation();
  const [deleteRenewalConfig] = useDeleteRenewalConfigMutation();

  const [query, setQuery] = useState('');
  const [selectedRenewalConfig, setSelectedRenewalConfig] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filteredRenewalConfigs, setFilteredRenewalConfigs] = useState(renewalConfigs);

  useEffect(() => {
    setFilteredRenewalConfigs(
      renewalConfigs.filter(rc => rc.name.toLowerCase().includes(query.toLowerCase())),
    );
  }, [query, renewalConfigs]);

  const onEdit = renewalConfig => {
    setSelectedRenewalConfig(renewalConfig);
    setShowEditModal(true);
  };

  const saveRenewalConfig = async renewalConfigData => {
    setLoading(true);
    setShowEditModal(false);
    if (renewalConfigData.id) {
      const { data: updatedRenewalConfig, error } = await updateRenewalConfig({
        id: renewalConfigData.id,
        data: renewalConfigData,
      });
      if (error) {
        showToast('Error', error.data.error.log, 'error');
        setLoading(false);
        return;
      }
      updateLocalRenewalConfigs(prev =>
        prev.map(rc => (rc.id === renewalConfigData.id ? updatedRenewalConfig : rc)),
      );
    } else {
      const { data: newRenewalConfig, error } = await createRenewalConfig(renewalConfigData);
      if (error) {
        showToast('Error', error.data.error.log, 'error');
        setLoading(false);
        return;
      }
      updateLocalRenewalConfigs(prev => [...prev, newRenewalConfig]);
      showToast(t('renewals.tabs.configuration.created'), '', 'success');
    }
    setQuery('');
    setLoading(false);
  };

  const handleDeleteRenewalConfig = async renewalConfigId => {
    setLoading(true);
    try {
      await deleteRenewalConfig(renewalConfigId).unwrap();
      updateLocalRenewalConfigs(prev => prev.filter(rc => rc.id !== renewalConfigId));
      setQuery('');
    } catch (error) {
      console.error('Failed to delete renewal config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRenewalConfigDisabled = async renewalConfig => {
    setLoading(true);
    const { data: updatedRenewalConfig, error } = await updateRenewalConfig({
      id: renewalConfig.id,
      data: { disabled: !renewalConfig.disabled },
    });
    if (error) {
      showToast('Error', error.data.error.log, 'error');
    } else {
      updateLocalRenewalConfigs(prev =>
        prev.map(rc => (rc.id === renewalConfig.id ? updatedRenewalConfig : rc)),
      );
    }
    setLoading(false);
  };

  return (
    <div className="u-padding--large">
      <RenewalConfigModal
        companyUsers={companyUsers}
        brands={brands}
        renewalConfig={selectedRenewalConfig}
        companyChecks={companyChecks}
        visible={showEditModal}
        onSubmit={saveRenewalConfig}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRenewalConfig(false);
        }}
      />
      <div
        className={cn(
          'card',
          'card-with-border',
          'u-padding',
          'd-flex',
          'justify-content-between',
        )}>
        <SearchInput placeholder="Search..." className="u-width-25" onChange={setQuery} />
        <div className="d-flex">
          <Button
            icon="plus"
            onClick={() => {
              setSelectedRenewalConfig(null);
              setShowEditModal(true);
            }}>
            {t('renewals.tabs.configuration.create_renewal_type')}
          </Button>
        </div>
      </div>
      <div className="u-margin-top--large">
        {filteredRenewalConfigs.map(renewalConfig => {
          const companyCheck = companyChecks.find(cc => cc.type === renewalConfig.type);
          return (
            <RenewalConfigRow
              key={renewalConfig.id}
              companyCheck={companyCheck}
              renewalConfig={renewalConfig}
              onEdit={onEdit}
              onDelete={handleDeleteRenewalConfig}
              onToggleDisabled={toggleRenewalConfigDisabled}
            />
          );
        })}
      </div>
    </div>
  );
};

export const RenewalConfigRow = ({
  companyCheck,
  renewalConfig,
  onEdit = () => {},
  onDelete = () => {},
  onToggleDisabled = () => {},
}) => {
  const { t } = useTranslation();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showDisableAlert, setShowDisableAlert] = useState(false);

  const handleDelete = () => {
    setShowDeleteAlert(false);
    onDelete(renewalConfig.id);
  };

  const handleDisable = () => {
    setShowDisableAlert(false);
    onToggleDisabled(renewalConfig);
  };

  const handleToggleDisabled = () => {
    if (renewalConfig.disabled) {
      onToggleDisabled(renewalConfig);
    } else {
      setShowDisableAlert(true);
    }
  };

  const frequencyLabel =
    renewalConfig.renewal_mode === 'conditional'
      ? t('renewals.conditional')
      : renewalConfig.renewal_mode === 'use_expiration'
        ? t('renewals.expiration_date')
        : t('renewals.every_x_days', { days: renewalConfig.frequency });

  const isDisabled = renewalConfig.disabled || renewalConfig.blocked;

  return (
    <>
      <Alert
        show={showDeleteAlert}
        title={`Are you sure you want to delete "${renewalConfig.name}"?`}
        message="This action cannot be undone. All configurations associated with this renewal type will be permanently removed."
        ok="Delete"
        type="error"
        onOk={handleDelete}
        icon={Delete02Icon}
        cancel="Cancel"
        onCancel={() => setShowDeleteAlert(false)}
      />
      <Alert
        show={showDisableAlert}
        title={t('renewals.disable_confirm_title', { name: renewalConfig.name })}
        message={t('renewals.disable_confirm_message')}
        ok={t('renewals.disable')}
        type="warning"
        onOk={handleDisable}
        cancel={t('common.cancel')}
        onCancel={() => setShowDisableAlert(false)}
      />
      <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
        <div className={cn('d-flex', 'justify-content-between', 'align-items-center')}>
          <div
            className={cn('d-flex', 'align-items-center', {
              [styles.rowDisabled]: isDisabled,
            })}>
            <IconComponent icon={getCheckTypeIcon(renewalConfig.type)} className="u-margin-right" />
            <div>
              <h1 className="title-4">{renewalConfig.name}</h1>
              <p className={cn('t-small', 'u-margin-0')}>{companyCheck?.check_type?.title}</p>
            </div>
          </div>
          <div className={cn('d-flex', 'align-items-center')}>
            <div
              className={cn('d-flex', 'align-items-center', {
                [styles.rowDisabled]: isDisabled,
              })}>
              {renewalConfig.auto_renew && (
                <Badge
                  className={cn(
                    'u-status-completed',
                    'u-capitalize',
                    'u-font-size-small',
                    'd-inline-flex',
                    'align-items-center',
                  )}>
                  <HugeiconsIcon icon={ZapIcon} size={14} className="u-margin-right--small" />
                  {t('renewals.auto_renew')}
                </Badge>
              )}
              {renewalConfig.auto_assign && (
                <Badge
                  className={cn(
                    'u-status-primary',
                    'u-capitalize',
                    'u-font-size-small',
                    'd-inline-flex',
                    'align-items-center',
                    'u-margin-left',
                  )}>
                  <HugeiconsIcon icon={ReplayIcon} size={14} className="u-margin-right--small" />
                  {t('renewals.auto_assign')}
                </Badge>
              )}
              <Badge
                className={cn(
                  'u-status-primary',
                  'u-capitalize',
                  'u-font-size-small',
                  'd-inline-flex',
                  'align-items-center',
                  'u-margin-left',
                )}>
                <HugeiconsIcon icon={Clock02Icon} size={14} className="u-margin-right--small" />
                {frequencyLabel}
              </Badge>
              {renewalConfig.blocked ? (
                <Badge
                  className={cn(
                    'u-status-canceled',
                    'u-capitalize',
                    'u-font-size-small',
                    'u-margin-left',
                    'd-inline-flex',
                    'align-items-center',
                  )}>
                  {t('renewals.blocked')}
                </Badge>
              ) : (
                <Badge
                  className={cn(
                    renewalConfig.disabled ? 'u-status-canceled' : 'u-status-completed',
                    'u-capitalize',
                    'u-font-size-small',
                    'u-margin-left',
                    'd-inline-flex',
                    'align-items-center',
                  )}>
                  <span
                    className={cn(
                      styles.statusDot,
                      renewalConfig.disabled ? styles.dotDisabled : styles.dotEnabled,
                    )}
                  />
                  {renewalConfig.disabled ? t('renewals.disabled') : t('renewals.enabled')}
                </Badge>
              )}
            </div>
            <ActionsMenu newIconLibrary className="u-margin-left">
              {!renewalConfig.blocked && (
                <ActionsMenu.Item
                  icon={PowerIcon}
                  title={renewalConfig.disabled ? t('renewals.enable') : t('renewals.disable')}
                  type={renewalConfig.disabled ? 'success' : 'secondary'}
                  newIconLibrary
                  onClick={handleToggleDisabled}
                />
              )}
              {!renewalConfig.blocked && (
                <ActionsMenu.Item
                  icon={PencilEdit01Icon}
                  title={t('common.edit')}
                  type="secondary"
                  newIconLibrary
                  onClick={() => onEdit(renewalConfig)}
                />
              )}
              <ActionsMenu.Item
                icon={Delete02Icon}
                title={t('common.delete')}
                type="error"
                newIconLibrary
                onClick={() => setShowDeleteAlert(true)}
              />
            </ActionsMenu>
          </div>
        </div>
      </div>
    </>
  );
};
